import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Search, Sparkles, Loader2, ScrollText, BookOpenCheck, Quote, MapPinned } from "lucide-react";
import { EsvPassage } from "@/components/EsvPassage";
import { PassageMaps } from "@/components/PassageMaps";
import { MapBrowser } from "@/components/MapBrowser";
import { Link } from "react-router-dom";

const WORD_CHIPS = ["Hesed", "Logos", "Agape", "Ruach", "Shalom", "Charis"];
const PARABLE_CHIPS = ["Parable of the Sower", "The Prodigal Son", "Matthew 5:13-16 (Salt & Light)", "Romans 8:28"];

export default function WordStudy() {
  return (
    <div data-testid="word-study-page" className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 pb-10">
      <div className="text-center max-w-2xl mx-auto animate-rise">
        <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold mb-4">
          <Sparkles className="w-4 h-4" /> AI-assisted, Scripture-rooted
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl font-700 text-ink leading-tight">Bible Word Study</h1>
        <p className="mt-4 text-ink/70 leading-relaxed">
          Search the original Hebrew, Greek, and Aramaic behind the text, ask for a faithful,
          Christ-centered explanation of any parable or passage, and see where it happened on over
          1,000 Bible maps from the Casual English Bible® Atlas.
        </p>
      </div>

      <Tabs defaultValue="word" className="mt-10">
        <TabsList data-testid="word-study-mode-tab" className="grid grid-cols-3 w-full max-w-xl mx-auto bg-card border border-border rounded-full p-1 h-12">
          <TabsTrigger value="word" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-parchment font-600 text-xs sm:text-sm px-2 whitespace-normal leading-tight">
            Original Words
          </TabsTrigger>
          <TabsTrigger value="parable" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-parchment font-600 text-xs sm:text-sm px-2 whitespace-normal leading-tight">
            Parables & Passages
          </TabsTrigger>
          <TabsTrigger value="places" data-testid="word-study-places-tab" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-parchment font-600 text-xs sm:text-sm px-2 whitespace-normal leading-tight">
            Places & Maps
          </TabsTrigger>
        </TabsList>

        <TabsContent value="word" className="mt-8">
          <WordSearch />
        </TabsContent>
        <TabsContent value="parable" className="mt-8">
          <ParableSearch />
        </TabsContent>
        <TabsContent value="places" className="mt-8">
          <PlaceSearch />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const MAX_WAIT_MS = 4 * 60 * 1000;

async function waitForStudy(first, isCurrent) {
  let data = first;
  const started = Date.now();
  while (data.status === "pending") {
    if (Date.now() - started > MAX_WAIT_MS) throw new Error("timeout");
    await sleep(2500);
    if (!isCurrent()) return null;
    data = (await api.get(`/word-study/jobs/${first.job_id}`)).data;
  }
  if (data.status === "error") throw new Error(data.detail || "error");
  return data.result;
}

function useLookup(endpoint) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const runId = useRef(0);
  const run = async (query) => {
    if (!query.trim()) return;
    const id = ++runId.current;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post(endpoint, { query });
      const res = await waitForStudy(data, () => id === runId.current);
      if (id === runId.current && res) setResult(res);
    } catch (e) {
      if (id === runId.current) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "The study could not be completed. Please try again.");
      }
    } finally {
      if (id === runId.current) setLoading(false);
    }
  };
  return { loading, result, run };
}

function SearchBar({ value, onChange, onSubmit, placeholder, testid, textarea }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {textarea ? (
        <Textarea
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-card border-ink/15 min-h-[56px] flex-1 rounded-2xl"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSubmit(); }}
        />
      ) : (
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
          <Input
            data-testid={testid}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="pl-11 h-14 rounded-full bg-card border-ink/15"
            onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
          />
        </div>
      )}
      <Button
        onClick={onSubmit}
        data-testid="word-study-submit"
        className="h-14 rounded-full bg-gold hover:bg-ink text-ink hover:text-parchment font-700 px-8"
      >
        <Sparkles className="w-4 h-4 mr-2" /> Study
      </Button>
    </div>
  );
}

function Chips({ chips, onPick }) {
  return (
    <div className="flex flex-wrap gap-2 mt-4 justify-center">
      {chips.map((c) => (
        <button
          key={c}
          data-testid={`word-study-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onPick(c)}
          className="px-4 py-1.5 rounded-full text-sm bg-card border border-border text-ink/70 hover:border-gold hover:text-ink transition-colors"
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function Loading() {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="word-study-loading" className="flex flex-col items-center py-16 text-ink/50 text-center">
      <Loader2 className="w-8 h-8 animate-spin text-gold" />
      <p className="mt-3 font-mono text-xs uppercase tracking-widest">Searching the Scriptures… {secs}s</p>
      <p className="mt-2 text-sm text-ink/55 max-w-sm">
        A new study is written fresh and can take up to a minute. Studies people have looked up before open instantly.
      </p>
    </div>
  );
}

function WordSearch() {
  const [q, setQ] = useState("");
  const { loading, result, run } = useLookup("/word-study/word");

  return (
    <div>
      <SearchBar
        value={q} onChange={setQ} onSubmit={() => run(q)}
        placeholder="Search Hebrew / Greek / English (e.g., Agape, Logos, Hesed)…"
        testid="word-study-input"
      />
      <Chips chips={WORD_CHIPS} onPick={(c) => { setQ(c); run(c); }} />

      {loading && <Loading />}

      {result && !loading && (
        <div className="mt-10 animate-rise" data-testid="word-study-result">
          <div className="bg-ink-deep text-parchment rounded-3xl p-8 sm:p-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <span className="font-mono text-[11px] uppercase tracking-widest text-gold">{result.language}</span>
                <h2 className="font-serif text-4xl font-600 mt-1">{result.word}</h2>
              </div>
              <span className="font-serif text-5xl sm:text-6xl text-gold">{result.original_script}</span>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              {result.transliteration && <Meta label="Transliteration" value={result.transliteration} />}
              {result.strongs && <Meta label="Strong's" value={result.strongs} />}
              {result.part_of_speech && <Meta label="Part of Speech" value={result.part_of_speech} />}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-5">
            <Panel icon={ScrollText} title="Root & Literal Meaning">{result.root_definition}</Panel>
            <Panel icon={BookOpenCheck} title="Theological & Gospel Context">{result.theological_context}</Panel>
          </div>

          {result.occurrences?.length > 0 && (
            <div className="bg-card rounded-2xl border border-border p-7 mt-5">
              <h3 className="font-serif text-2xl font-600 text-ink mb-4">Biblical Occurrences</h3>
              <div className="space-y-3">
                {result.occurrences.map((o) => (
                  <div key={`${o.reference}-${o.note}`} className="flex gap-4 items-start">
                    <span className="font-mono text-xs text-gold whitespace-nowrap mt-1 min-w-[6rem]">{o.reference}</span>
                    <p className="text-sm text-ink/75 leading-relaxed">{o.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ParableSearch() {
  const [q, setQ] = useState("");
  const { loading, result, run } = useLookup("/word-study/parable");

  return (
    <div>
      <SearchBar
        value={q} onChange={setQ} onSubmit={() => run(q)}
        placeholder="Enter a parable or passage (e.g., Parable of the Sower, John 3:16)…"
        testid="word-study-input" textarea
      />
      <Chips chips={PARABLE_CHIPS} onPick={(c) => { setQ(c); run(c); }} />

      {loading && <Loading />}

      {result && !loading && (
        <div className="mt-10 animate-rise" data-testid="word-study-result">
          <div className="bg-ink-deep text-parchment rounded-3xl p-8 sm:p-10">
            <span className="font-mono text-[11px] uppercase tracking-widest text-gold">{result.reference}</span>
            <h2 className="font-serif text-4xl font-600 mt-1">{result.title}</h2>
          </div>

          {result.reference && (
            <div className="bg-gold-light rounded-2xl border border-gold/30 p-7 mt-5">
              <EsvPassage reference={result.reference} />
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5 mt-5">
            <Panel icon={ScrollText} title="Historical & Cultural Context">{result.historical_context}</Panel>
            <Panel icon={Quote} title="What the Original Audience Heard">{result.original_audience}</Panel>
          </div>

          {result.meaning?.length > 0 && (
            <div className="bg-card rounded-2xl border border-border p-7 mt-5">
              <h3 className="font-serif text-2xl font-600 text-ink mb-4">Verse-by-Verse Meaning</h3>
              <div className="space-y-4">
                {result.meaning.map((m) => (
                  <div key={`${m.part}-${m.explanation}`} className="border-l-2 border-gold pl-4">
                    <p className="font-mono text-xs text-olive mb-1">{m.part}</p>
                    <p className="text-sm text-ink/75 leading-relaxed">{m.explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <PassageMaps places={result.places || []} book={result.book || ""} reference={result.reference || ""} />

          <div className="bg-gold-light rounded-2xl border border-gold/30 p-7 mt-5">
            <div className="flex items-center gap-2 mb-3">
              <BookOpenCheck className="w-5 h-5 text-gold" />
              <h3 className="font-serif text-2xl font-600 text-ink">Gospel & Christ-Centered Application</h3>
            </div>
            <p className="text-ink/80 leading-relaxed">{result.gospel_application}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const PLACE_CHIPS = ["Jerusalem", "Bethlehem", "Galilee", "Jericho", "Egypt", "Babylon", "Nazareth"];

function PlaceSearch() {
  const [q, setQ] = useState("");
  const [place, setPlace] = useState("");
  const go = (v) => { const t = (v ?? q).trim(); if (t) { setQ(t); setPlace(t); } };
  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
          <Input data-testid="place-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") go(); }}
            placeholder="Search a place (e.g. Jerusalem, Bethlehem, Sea of Galilee)…"
            className="pl-11 h-14 rounded-full bg-card border-ink/15" />
        </div>
        <Button onClick={() => go()} data-testid="place-search-submit"
          className="h-14 rounded-full bg-gold hover:bg-ink text-ink hover:text-parchment font-700 px-8">
          <MapPinned className="w-4 h-4 mr-2" /> Find Maps
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-4 justify-center">
        {PLACE_CHIPS.map((c) => (
          <button key={c} data-testid={`place-chip-${c.toLowerCase()}`} onClick={() => go(c)}
            className="px-4 py-1.5 rounded-full text-sm bg-card border border-border text-ink/70 hover:border-gold hover:text-ink transition-colors">
            {c}
          </button>
        ))}
      </div>
      <p className="text-center mt-4 text-sm text-ink/55">
        Or <Link to="/bible-maps" data-testid="place-search-gallery-link" className="text-olive hover:text-gold underline-offset-4 hover:underline">browse the whole atlas book by book</Link>.
      </p>
      <div className="mt-10">
        <MapBrowser place={place} testid="place-search-results" />
      </div>
    </div>
  );
}

const Meta = ({ label, value }) => (
  <div className="bg-parchment/10 rounded-xl px-4 py-2 border border-parchment/15">
    <p className="font-mono text-[10px] uppercase tracking-widest text-parchment/50">{label}</p>
    <p className="font-600 text-parchment">{value}</p>
  </div>
);

const Panel = ({ icon: Icon, title, children }) => (
  <div className="bg-card rounded-2xl border border-border p-7">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-5 h-5 text-gold" />
      <h3 className="font-serif text-2xl font-600 text-ink">{title}</h3>
    </div>
    <p className="text-ink/75 leading-relaxed">{children}</p>
  </div>
);
